import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import {
  detectGeo,
  isLangUserChosen,
  isCurrencyUserChosen,
  isNavigatorLangSupported,
  getUrlLangParam,
  markLangUserChosen,
} from '../utils/geoDetect';

/**
 * Runs once on app boot. Detects the visitor's country and applies a
 * sensible language + currency, but only if the user has not explicitly
 * chosen them yet. Shows a small dismissible banner so the choice stays
 * transparent.
 */
const COUNTRY_FLAG = {
  SK: '🇸🇰', CZ: '🇨🇿', DE: '🇩🇪', AT: '🇦🇹', CH: '🇨🇭',
  PL: '🇵🇱', HU: '🇭🇺', RU: '🇷🇺', UA: '🇺🇦', BY: '🇧🇾', KZ: '🇰🇿',
  US: '🇺🇸', CA: '🇨🇦', GB: '🇬🇧', IE: '🇮🇪', AU: '🇦🇺', NZ: '🇳🇿',
};

const GeoInit = () => {
  const { i18n } = useTranslation();
  const { currency, setCurrency } = useCart();
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Highest priority: explicit ?lang=XX in URL. Applies immediately and is
    // remembered as a user choice so subsequent IP/navigator logic never
    // overrides it.
    const urlLang = getUrlLangParam();
    if (urlLang && i18n.resolvedLanguage?.split('-')[0] !== urlLang) {
      i18n.changeLanguage(urlLang);
      try { localStorage.setItem('i18nextLng', urlLang); } catch { /* ignore */ }
      markLangUserChosen();
    }

    (async () => {
      const profile = await detectGeo();
      if (cancelled || !profile) return;

      // Variant A: browser/OS language wins over IP geo. Only let the IP-derived
      // country force a different language when the user's navigator language
      // is NOT one of our supported UI languages (sk/cs/de/en/ru) AND the user
      // has not explicitly chosen one.
      const langChanged =
        !isLangUserChosen() &&
        !isNavigatorLangSupported() &&
        profile.language &&
        i18n.resolvedLanguage?.split('-')[0] !== profile.language;

      const currencyChanged =
        !isCurrencyUserChosen() &&
        profile.currency &&
        profile.currency !== currency;

      if (langChanged) {
        i18n.changeLanguage(profile.language);
        try { localStorage.setItem('i18nextLng', profile.language); } catch { /* ignore */ }
      }
      if (currencyChanged) {
        setCurrency(profile.currency);
      }

      // Persist detected shipping country name for Checkout prefill
      if (profile.country_name) {
        try { localStorage.setItem('sunify_geo_country_name', profile.country_name); } catch { /* ignore */ }
      }

      // Show banner only once per session and only when something actually changed
      const sessionFlag = sessionStorage.getItem('sunify_geo_banner_shown');
      if (!sessionFlag && (langChanged || currencyChanged) && profile.source === 'ip') {
        setBanner({
          country_code: profile.country_code,
          country_name: profile.country_full_name || profile.country_code,
          language: profile.language,
          currency: profile.currency,
        });
        try { sessionStorage.setItem('sunify_geo_banner_shown', '1'); } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!banner) return null;

  const flag = COUNTRY_FLAG[banner.country_code] || '🌐';

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] max-w-sm bg-white border border-gray-200 shadow-xl rounded-lg p-4 text-sm text-gray-800 flex items-start space-x-3"
      data-testid="geo-banner"
    >
      <span className="text-2xl leading-none">{flag}</span>
      <div className="flex-1">
        <div className="font-semibold mb-1" data-testid="geo-banner-title">
          {banner.country_name}
        </div>
        <div className="text-gray-600 mb-2">
          {`${banner.language.toUpperCase()} · ${banner.currency}`}
        </div>
        <button
          type="button"
          onClick={() => setBanner(null)}
          className="text-xs text-orange-600 hover:text-orange-700 font-medium"
          data-testid="geo-banner-dismiss"
        >
          OK
        </button>
      </div>
      <button
        type="button"
        onClick={() => setBanner(null)}
        aria-label="Close"
        className="text-gray-400 hover:text-gray-600 leading-none"
        data-testid="geo-banner-close"
      >
        ×
      </button>
    </div>
  );
};

export default GeoInit;
